import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createPaymentMode,
  updatePaymentMode,
  fetchPaymentModeById,
  resetPaymentModeStatuses,
} from "../../store/slices/paymentModesSlice";
import { TextField, TextAreaField, SelectField } from "../../components/ui/FormControls";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

const schema = yup.object({
  name: yup.string().required("Payment mode name is required"),
  description: yup.string().nullable(),
  status: yup.string().required("Status is required"),
});

export default function AddPaymentModePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editPaymentModeId = searchParams.get("edit");
  const isEditMode = Boolean(editPaymentModeId);

  const {
    paymentMode,
    paymentModeCreateStatus,
    paymentModeFetchStatus,
    paymentModeUpdateStatus,
    loading,
  } = useSelector((state) => state.paymentModes || {});

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      status: "true",
    },
  });

  useEffect(() => {
    if (isEditMode && editPaymentModeId) {
      dispatch(fetchPaymentModeById(editPaymentModeId));
    }
  }, [dispatch, isEditMode, editPaymentModeId]);

  useEffect(() => {
    if (isEditMode && paymentMode && paymentModeFetchStatus === "success") {
      reset({
        name: paymentMode.name || "",
        description: paymentMode.description || "",
        status: paymentMode.status ? "true" : "false",
      });
    }
  }, [isEditMode, paymentMode, paymentModeFetchStatus, reset]);

  const onSubmitHandler = async (data) => {
    const payload = {
      name: data.name,
      description: data.description || "",
      status: data.status,
    };

    try {
      if (isEditMode) {
        await dispatch(updatePaymentMode({ uuid: editPaymentModeId, data: payload })).unwrap();
        showApiSuccess("Payment mode updated successfully");
      } else {
        await dispatch(createPaymentMode(payload)).unwrap();
        showApiSuccess("Payment mode created successfully");
      }
      navigate("/payment-modes");
    } catch (error) {
      console.log('Caught error:', error);
      console.log('Error type:', typeof error);
      // When using unwrap(), the error is the rejected value directly (err.response.data from the thunk)
      showApiError("Failed to save payment mode", error);
    }
  };



  if (isEditMode && paymentModeFetchStatus === "loading") {
    return <Loader />;
  }

  if (isEditMode && paymentModeFetchStatus === "failed") {
    return (
      <div className="max-w-2xl mx-auto theme-card rounded-lg p-6 text-center">
        <h2 className="text-xl font-bold text-red-600 mb-2">Payment Mode Not Found</h2>
        <p className="text-gray-600 mb-4">
          The payment mode you're trying to edit could not be found.
        </p>
        <button
          onClick={() => navigate("/payment-modes")}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Back to Payment Modes
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {loading && <Loader />}
      <div className="theme-card rounded-lg p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? "Edit Payment Mode" : "Add New Payment Mode"}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode
              ? "Update payment mode information"
              : "Fill in the details to add a new payment mode"}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6">
          <TextField
            label="Payment Mode Name"
            required
            placeholder="e.g., Cash, Card, UPI"
            {...register("name")}
            error={errors?.name?.message}
          />

          <TextAreaField
            label="Description"
            rows={3}
            placeholder="Optional description for this payment mode"
            {...register("description")}
            error={errors?.description?.message}
          />

          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <SelectField
                label="Status"
                required
                value={field.value}
                onChange={field.onChange}
                options={[
                  { label: "Active", value: "true" },
                  { label: "Inactive", value: "false" },
                ]}
                error={errors?.status?.message}
              />
            )}
          />

          <div className="flex items-center justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => navigate("/payment-modes")}
              className="theme-button-secondary px-6 py-2 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {isEditMode ? "Update Payment Mode" : "Create Payment Mode"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
